import { getUserData } from 'utils/functions/getUserData';
import { EventData, EventName } from 'utils/hooks/useTrackEvent/types';

// Returns minimal sanitized props for Amplitude based on allowlisted events.
// Defaults: user_id, school_id. Adds specific IDs when available.
export function formatAmplitudeProps(
  eventName: EventName,
  payload?: EventData
): Record<string, unknown> {
  const { userRole } = getUserData();
  const base = {
    school_id: userRole?.school,
  } as Record<string, unknown>;

  function withDefined(obj: Record<string, unknown>) {
    const cleaned: Record<string, unknown> = {};

    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null) cleaned[k] = v;
    });

    return cleaned;
  }

  switch (eventName) {
    case 'test_page_access':
      return withDefined({
        ...base,
        test_id: payload?.test?.test_id,
        test_status: payload?.test?.test_status,
      });
    case 'click_on_test_type_tab':
      return withDefined({
        ...base,
        test_id: payload?.test?.test_id,
        test_status: payload?.test?.test_status,
      });
    case 'learning_path_report_access':
    case 'evolution_report_page_access':
    case 'grade_and_proficiency_level_card_click':
    case 'cognitive_axes_saeb_card_click':
    case 'student_engagement_card_click':
    case 'learning_path_BNCC_skills_button_click':
    case 'learning_path_access_student_list_button_click':
    case 'learning_path_access_engagement_button_click':
    case 'learning_path_in_progress_composition_access':
    case 'learning_path_report_open_steps_modal_click':
      return withDefined({
        ...base,
        learning_path_instance_id:
          payload?.learningPathInstance?.learning_path_instance_id,
        learning_path_status:
          payload?.learningPathInstance?.learning_path_status,
        composition_id: payload?.composition?.composition_id,
        school_id: payload?.school?.school_id,
      });
    case 'reviewed_composition_access':
      return withDefined({
        ...base,
        test_id: payload?.test?.test_id,
        composition_id: payload?.composition?.composition_id,
      });
    case 'composition_review_edited':
      return withDefined({
        ...base,
        review_id: payload?.review?.review_id,
        competence_id: payload?.review?.competence_id,
      });
    case 'reopen_test_button_click':
      return withDefined({
        ...base,
        test_id: payload?.test?.test_id,
      });

    case 'baixo_engajamento_click':
    case 'bom_desempenho_click':
    case 'com_dificuldades_click':
    case 'learning_path_quick_access_click':
    case 'create_learning_path_click':
    case 'redirect_to_evolution_report_click':
    case 'reminder_click':
    case 'redirect_to_support_material_click':
    case 'redirect_to_learning_path_report':
    case 'redirect_to_rewrite_report':
    case 'redirect_to_learning_path_report_click':
    case 'learning_path_list_page_access':
    case 'teacher_login':
    case 'learning_path_report_bncc_skills_click':
    case 'learning_path_report_theme_click':
    case 'learning_path_report_questionnaire_content_click':
    case 'learning_path_report_indicators_click':
    case 'write_support_material_click':
    case 'questionnaire_support_material_click':
    case 'learning_path_report_write_tab_click':
    case 'learning_path_report_questionnaire_tab_click':
    case 'learning_path_report_rewrite_tab_click':
    case 'learning_path_report_low_performance_write_click':
    case 'learning_path_report_low_performance_questionnaire_click':
      return withDefined(base);

    case 'home_page_access':
      return withDefined({ base, ...payload?.user });

    case 'learning_path_card_expanded':
    case 'student_support_material_click':
    case 'teacher_support_material_click':
      return withDefined({
        ...base,
        order: payload?.order,
        page: payload?.page,
      });

    case 'home_modal_filters':
    case 'learning_path_list_page_filters':
    case 'learning_path_report_student_performance_filters':
      return withDefined({
        ...base,
        filters: payload?.filters,
        order: payload?.order,
        page: payload?.page,
      });

    case 'learning_path_report_composition_details_click':
      return withDefined({
        ...base,
        student_id: payload?.student?.student_id,
      });

    default:
      return withDefined(base);
  }
}
